// Modelo y lógica de "planes de tratamiento" (Opción C).
//
// Un paciente pasa de tener un presupuesto plano (`paciente.presupuesto`, un array
// de tratamientos con sus pagos adentro) a tener VARIOS planes independientes
// (`paciente.presupuestos`), cada uno con su fecha, estado, número y sus tratamientos.
//
// Este módulo es lógica pura (sin Firestore ni Vue): sirve tanto para el store
// (normalizar al cargar, recalcular estados) como para la UI (totales, saldos).
// La persistencia y las transacciones viven en el store.

function uuid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function formatNum(n) {
  return String(n).padStart(4, "0");
}

// --- Cálculo por tratamiento (item) ---

export function precioFinalItem(item) {
  const precio = Number(item?.precio) || 0;
  const desc = Math.min(100, Math.max(0, Number(item?.descuento) || 0));
  return precio - (precio * desc) / 100;
}

export function pagadoItem(item) {
  if (Array.isArray(item?.pagos)) {
    return item.pagos.reduce((s, p) => s + (Number(p?.monto) || 0), 0);
  }
  // Formato viejo sin array de pagos: caer al total guardado.
  return Number(item?.pagado) || 0;
}

export function faltaItem(item) {
  return Math.max(0, precioFinalItem(item) - pagadoItem(item));
}

export function estadoItem(item) {
  const pf = precioFinalItem(item);
  const pg = pagadoItem(item);
  if (pg >= pf - 0.001 && (pf > 0 || pg > 0)) return "pagado";
  if (pg > 0) return "parcial";
  return "pendiente";
}

// --- Cálculo por plan ---

export function planTotal(plan) {
  return (plan?.items || []).reduce((s, it) => s + precioFinalItem(it), 0);
}

export function planPagado(plan) {
  return (plan?.items || []).reduce((s, it) => s + pagadoItem(it), 0);
}

export function planSaldo(plan) {
  return Math.max(0, planTotal(plan) - planPagado(plan));
}

export function planTienePagos(plan) {
  return (plan?.items || []).some(
    (it) => Array.isArray(it.pagos) && it.pagos.length > 0,
  );
}

// Estado del plan derivado del saldo:
// - 'cancelado' es pegajoso (elección manual): nunca se pisa.
// - un plan sin pagos queda como estaba (arranca 'borrador' hasta el primer cobro).
// - con el primer pago pasa a 'pendiente' (En curso); saldado, a 'finalizado'.
export function estadoPlanAuto(plan) {
  if (plan.estado === "cancelado") return plan.estado;
  const items = plan.items || [];
  if (items.length && planSaldo(plan) <= 0.001 && planPagado(plan) > 0) return "finalizado";
  if (planPagado(plan) > 0) return "pendiente";
  return plan.estado || "borrador";
}

// --- Normalización de un pago / item / plan (asegura ids y forma consistente) ---

function normalizarPago(p) {
  const pago = {
    id: p?.id || uuid(),
    fecha: p?.fecha || new Date().toISOString(),
    monto: Number(p?.monto) || 0,
  };
  if (p?.metodo) pago.metodo = p.metodo;
  if (p?.esSaldoAnterior) pago.esSaldoAnterior = true;
  return pago;
}

function normalizarItem(rawItem) {
  const pagos = Array.isArray(rawItem?.pagos) ? rawItem.pagos.map(normalizarPago) : [];
  // Formato viejo: tenía 'pagado' pero no un array de pagos. Lo convertimos en
  // un pago inicial "saldo anterior" para no perder cuánto se había cobrado.
  if (pagos.length === 0 && Number(rawItem?.pagado) > 0) {
    pagos.push({
      id: uuid(),
      fecha: rawItem.fecha || new Date().toISOString(),
      monto: Number(rawItem.pagado),
      esSaldoAnterior: true,
    });
  }
  return {
    id: rawItem?.id || uuid(),
    tratamientoId: rawItem?.tratamientoId || null,
    nombre: rawItem?.nombre || "",
    precio: Number(rawItem?.precio) || 0,
    descuento: Math.min(100, Math.max(0, Number(rawItem?.descuento) || 0)),
    pagos,
  };
}

function normalizarPlan(rawPlan, numPorDefecto) {
  const items = Array.isArray(rawPlan?.items) ? rawPlan.items.map(normalizarItem) : [];
  const plan = {
    id: rawPlan?.id || uuid(),
    num: rawPlan?.num || formatNum(numPorDefecto),
    fecha: rawPlan?.fecha || new Date().toISOString(),
    estado: rawPlan?.estado || "pendiente",
    items,
  };
  // Si no traía estado explícito, derivarlo del saldo.
  if (!rawPlan?.estado) plan.estado = estadoPlanAuto(plan);
  return plan;
}

function fechaCreacionPaciente(paciente) {
  const f = paciente?.fechaCreacion || paciente?.creadoEn || paciente?.fechaRegistro;
  if (f && typeof f.toDate === "function") return f.toDate().toISOString();
  if (typeof f === "string") return f;
  return new Date().toISOString();
}

/**
 * Devuelve `{ presupuestos, proximoPlanNum, migrado }` para un paciente, SIN mutarlo.
 *
 * - Si ya tiene `presupuestos` (formato nuevo): lo normaliza (asegura ids) → migrado=false.
 * - Si solo tiene `presupuesto` (formato viejo, array plano) con datos: lo envuelve en
 *   UN plan histórico con todos sus tratamientos y pagos intactos → migrado=true.
 * - Si no tiene ninguno (o el viejo está vacío): devuelve una lista vacía → migrado=false.
 *
 * `migrado=true` es la señal de que conviene persistir la migración en Firestore.
 */
export function normalizarPlanes(paciente) {
  if (Array.isArray(paciente?.presupuestos)) {
    let maxNum = 0;
    const presupuestos = paciente.presupuestos.map((pl, i) => {
      const p = normalizarPlan(pl, i + 1);
      const n = parseInt(p.num, 10);
      if (Number.isFinite(n) && n > maxNum) maxNum = n;
      return p;
    });
    const proximoPlanNum = Number(paciente.proximoPlanNum) || maxNum + 1;
    return { presupuestos, proximoPlanNum, migrado: false };
  }

  const viejo = Array.isArray(paciente?.presupuesto) ? paciente.presupuesto : [];
  if (viejo.length === 0) {
    return { presupuestos: [], proximoPlanNum: 1, migrado: false };
  }

  const plan = {
    id: uuid(),
    num: formatNum(1),
    fecha: fechaCreacionPaciente(paciente),
    estado: "pendiente",
    items: viejo.map(normalizarItem),
  };
  // Un plan migrado representa trabajo ya presupuestado (no un borrador nuevo):
  // 'finalizado' si está todo pago, 'pendiente' (En curso) si le queda saldo.
  plan.estado = planSaldo(plan) <= 0.001 && planPagado(plan) > 0 ? "finalizado" : "pendiente";
  return { presupuestos: [plan], proximoPlanNum: 2, migrado: true };
}

// Crea un plan nuevo vacío (borrador). El número lo pasa el store desde proximoPlanNum.
export function crearPlanVacio(num) {
  return {
    id: uuid(),
    num: formatNum(num),
    fecha: new Date().toISOString(),
    estado: "borrador",
    items: [],
  };
}

// Crea un item de plan a partir de un tratamiento de la biblioteca (copia congelada
// de nombre y precio: cambiar el precio en la biblioteca no altera planes viejos).
export function crearItemDeTratamiento(tratamiento) {
  return {
    id: uuid(),
    tratamientoId: tratamiento?.id || null,
    nombre: tratamiento?.nombre || "",
    precio: Number(tratamiento?.precio) || 0,
    descuento: 0,
    pagos: [],
  };
}

export function nuevoPago(monto, metodo) {
  const pago = { id: uuid(), fecha: new Date().toISOString(), monto: Number(monto) || 0 };
  if (metodo) pago.metodo = metodo;
  return pago;
}

// --- Operaciones puras sobre el array `presupuestos` ---
// Mutan el array en el lugar y devuelven true si aplicaron, false si no corresponde
// (para abortar la transacción). Son id-dirigidas e idempotentes, así se aplican
// igual sobre el estado local y sobre lo que haya en el servidor.

function buscarPlan(presupuestos, planId) {
  return presupuestos.find((p) => p.id === planId);
}

export function opAddPlan(presupuestos, plan) {
  if (presupuestos.some((p) => p.id === plan.id)) return true; // ya está
  presupuestos.push(plan);
  return true;
}

export function opRemovePlan(presupuestos, planId) {
  const plan = buscarPlan(presupuestos, planId);
  if (!plan) return true; // ya no está
  if (planTienePagos(plan)) return false; // no se deshace un plan con pagos
  const i = presupuestos.indexOf(plan);
  presupuestos.splice(i, 1);
  return true;
}

export function opSetEstado(presupuestos, planId, estado) {
  const plan = buscarPlan(presupuestos, planId);
  if (!plan) return false;
  plan.estado = estado;
  return true;
}

export function opAddItem(presupuestos, planId, item) {
  const plan = buscarPlan(presupuestos, planId);
  if (!plan) return false;
  if (plan.items.some((it) => it.id === item.id)) return true; // ya está
  plan.items.push(item);
  plan.estado = estadoPlanAuto(plan);
  return true;
}

export function opRemoveItem(presupuestos, planId, itemId) {
  const plan = buscarPlan(presupuestos, planId);
  if (!plan) return false;
  const item = plan.items.find((it) => it.id === itemId);
  if (!item) return true; // ya no está
  if (pagadoItem(item) > 0) return false; // no se quita un tratamiento con pagos
  plan.items = plan.items.filter((it) => it.id !== itemId);
  plan.estado = estadoPlanAuto(plan);
  return true;
}

export function opSetDescuento(presupuestos, planId, itemId, pct) {
  const plan = buscarPlan(presupuestos, planId);
  const item = plan && plan.items.find((it) => it.id === itemId);
  if (!item) return false;
  item.descuento = Math.min(100, Math.max(0, Number(pct) || 0));
  plan.estado = estadoPlanAuto(plan);
  return true;
}

// pagosPorItem: [{ itemId, pago }] — los pagos ya vienen con id fijo.
export function opAddPagos(presupuestos, planId, pagosPorItem) {
  const plan = buscarPlan(presupuestos, planId);
  if (!plan) return false;
  pagosPorItem.forEach(({ itemId, pago }) => {
    const item = plan.items.find((it) => it.id === itemId);
    if (item && !item.pagos.some((pg) => pg.id === pago.id)) item.pagos.push(pago);
  });
  plan.estado = estadoPlanAuto(plan);
  return true;
}

export function opRemovePago(presupuestos, planId, itemId, pagoId) {
  const plan = buscarPlan(presupuestos, planId);
  const item = plan && plan.items.find((it) => it.id === itemId);
  if (!item) return false;
  item.pagos = item.pagos.filter((pg) => pg.id !== pagoId);
  plan.estado = estadoPlanAuto(plan);
  return true;
}

export function opMovePago(presupuestos, planId, fromItemId, toItemId, pagoId) {
  const plan = buscarPlan(presupuestos, planId);
  if (!plan) return false;
  const from = plan.items.find((it) => it.id === fromItemId);
  const to = plan.items.find((it) => it.id === toItemId);
  if (!from || !to) return false;
  if (to.pagos.some((pg) => pg.id === pagoId)) return true; // ya movido
  const pago = from.pagos.find((pg) => pg.id === pagoId);
  if (!pago) return true; // ya no está en origen
  from.pagos = from.pagos.filter((pg) => pg.id !== pagoId);
  to.pagos.push(pago);
  plan.estado = estadoPlanAuto(plan);
  return true;
}

// Reparte `monto` en cascada entre los itemIds (en orden): completa el primero,
// sigue con el próximo, el último puede quedar parcial. Devuelve los pagos concretos
// (con id ya generado) por item y el sobrante. Se calcula UNA vez desde el estado local.
export function distribuirCascada(plan, itemIds, monto, metodo) {
  let left = Number(monto) || 0;
  const pagos = [];
  itemIds.forEach((id) => {
    if (left <= 0) return;
    const item = plan.items.find((it) => it.id === id);
    if (!item) return;
    const falta = faltaItem(item);
    if (falta <= 0) return;
    const apply = Math.min(left, falta);
    pagos.push({ itemId: id, pago: nuevoPago(apply, metodo) });
    left -= apply;
  });
  return { pagos, sobrante: left };
}
